import React, { useEffect, useMemo, useState } from 'react';
import { BottomModal } from '../../../components';
import { SelectUOMDrawerContent } from './select-uom-drawer-content';
import { OrderLine, POLineData, UOM } from '@zonofi/zono-money-store';

const getLineProduct = (line?: OrderLine | POLineData) => {
  if (!line) return undefined;
  if ('productVariant' in line) {
    if (line?.productVariant?.additionalUOM && line?.productVariant?.baseUOM) {
      return line?.productVariant;
    }
    return {
      ...line.productVariant,
      additionalUOM: line?.additionalUOM,
      baseUOM: line?.baseUOM,
    };
  }
  return line?.product;
};

export const SelectUOMDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  onUomChange: (uom: UOM) => void;
  currentLine?: OrderLine | POLineData;
}> = ({ open, onClose, onUomChange, currentLine }) => {
  const lineProduct = useMemo(() => getLineProduct(currentLine), [currentLine]);
  const [currentUOM, setCurrentUOM] = useState<UOM | undefined>();

  const defaultUOM = useMemo<UOM | undefined>(() => {
    if (!lineProduct) return undefined;

    const additionalUOM =
      lineProduct?.additionalUOM ??
      lineProduct?.additionalUom ??
      lineProduct?.additionaluom;
    const baseUOM =
      lineProduct?.baseUOM ?? lineProduct?.baseUom ?? lineProduct?.baseuom;

    const packSize = lineProduct.packSize;
    const quantity = currentLine?.quantity ?? 0;

    if (!additionalUOM) {
      return { uom: baseUOM || 'PCS', type: 'base', packSize: 1 };
    }
    if (quantity < (packSize ?? 1)) {
      return { uom: baseUOM || 'PCS', type: 'base', packSize: 1 };
    }

    return {
      uom: additionalUOM,
      type: 'additional',
      packSize: packSize ?? 1,
    };
  }, [lineProduct, currentLine?.quantity]);

  useEffect(() => {
    if (defaultUOM) {
      setCurrentUOM(defaultUOM);
    }
  }, [defaultUOM]);

  const uomList = useMemo(() => {
    const additional =
      lineProduct?.additionalUOM ??
      lineProduct?.additionalUom ??
      lineProduct?.additionaluom;
    const base =
      lineProduct?.baseUOM ?? lineProduct?.baseUom ?? lineProduct?.baseuom;

    const list: UOM[] = [];
    list.push({ uom: base || 'PCS', type: 'base', packSize: 1 });
    if (additional)
      list.push({
        uom: additional,
        type: 'additional',
        packSize: lineProduct?.packSize ?? 1,
      });
    return list;
  }, [lineProduct]);

  return (
    <BottomModal
      title="Select UOM"
      leftText="Close"
      onLeftAction={onClose}
      isVisible={open}
      bottomButton="Apply"
      onBottomAction={() => {
        if (currentUOM) {
          onUomChange(currentUOM);
        }
      }}
      clearSearchOnClose={false}
      leftTextStyle="close-text"
    >
      <SelectUOMDrawerContent
        onUomChange={setCurrentUOM}
        uomList={uomList}
        selectedUOM={currentUOM}
        lineQty={currentLine?.quantity ?? 0}
      />
    </BottomModal>
  );
};
